/**
 * useAutoScroll Hook
 *
 * Custom hook for smooth auto-scrolling with DOM rendering delay.
 * Guides users through multi-step workflows.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - 300ms delay for DOM rendering
 * - Smooth scroll behavior
 */

import { useCallback } from 'react';
import { scrollToElement, scrollToSelector, scrollToTop } from '@/utils/autoScroll';

interface UseAutoScrollReturn {
  scrollTo: (elementId: string, delay?: number) => void;
  scrollToQuery: (selector: string, delay?: number) => void;
  scrollTop: () => void;
}

/**
 * Custom hook for auto-scroll functionality
 *
 * @returns Object with scroll functions
 */
export function useAutoScroll(): UseAutoScrollReturn {
  const scrollTo = useCallback((elementId: string, delay?: number) => {
    scrollToElement(elementId, delay);
  }, []);

  const scrollToQuery = useCallback((selector: string, delay?: number) => {
    scrollToSelector(selector, delay);
  }, []);

  const scrollTop = useCallback(() => {
    scrollToTop();
  }, []);

  return {
    scrollTo,
    scrollToQuery,
    scrollTop,
  };
}
